import Axios from "axios";
import { round } from "lodash-es";
import { useQuery } from "react-query";
import { useMemo } from "react";

import type { Segment } from "src/types";
import { segment as decimalScale } from "src/config/decimal-scales";
import { segmentKeys } from "src/query-keys";

export function useSegments(scenarioId: string | number) {
  async function queryFn(scenarioId: string | number) {
    const url = `/planning-api/scenarios/${scenarioId}/segments`;
    const response = await Axios.get<Segment[]>(url);
    return response.data.map(deserialize);
  }

  const query = useQuery(segmentKeys.all(scenarioId), () => queryFn(scenarioId));

  // Memoize the segments array to prevent unnecessary re-renders
  const memoizedSegments = useMemo(() => {
    return query.data || [];
  }, [query.data]);

  return {
    ...query,
    data: memoizedSegments,
  };
}

// Memoize the deserialize function to prevent creating new objects unnecessarily
const deserialize = (data: Segment): Segment => {
  return {
    ...data,
    height_m: round(data.height_m, decimalScale.height_m),
    speed_km_h: round(data.speed_km_h, decimalScale.speed_km_h),
    barrierHeight_m: round(data.barrierHeight_m, decimalScale.barrierHeight_m),
    ...(data.heightOld_m ? { heightOld_m: round(data.heightOld_m, decimalScale.height_m) } : {}),
    ...(data.speedOld_km_h ? { speedOld_km_h: round(data.speedOld_km_h, decimalScale.speed_km_h) } : {}),
    ...(data.barrierHeightOld_m
      ? { barrierHeightOld_m: round(data.barrierHeightOld_m, decimalScale.barrierHeight_m) }
      : {}),
    vehicleCounts: data.vehicleCounts.map((vehicleCount) => {
      return {
        ...vehicleCount,
        count: round(vehicleCount.count, decimalScale.vehicleCounts),
      };
    }),
    vehicleCountsOld: data.vehicleCountsOld?.map((vehicleCount) => {
      return {
        ...vehicleCount,
        count: round(vehicleCount.count, decimalScale.vehicleCounts),
      };
    }),
  };
};
